import * as React from 'react';
import { Link } from 'react-router-dom';

import { Machine } from 'models/Machine';
import { MachineHistory } from 'models/MachineHistory';
import { MachineMaintenance } from 'models/MachineMaintenance';
import { Translations } from 'models/Translations';
import { Translate } from 'infrastructure/mapping/Translate';
import { FuelType } from 'models/FuelType';
import { format } from 'app/components/common/BarChart';
import { calculateStatistics } from 'app/pages/machines/MachineStatistics';

interface Props {
    machines: Machine[];
    machineHistories: MachineHistory[];
    machineMaintenances: MachineMaintenance[];
    fuelTypes?: FuelType[];
    translations: Translations;
}

interface DueItem {
    name: string;
    remaining: number;
    unit: string;
    share: number; // part of the interval still left, the nearer limit decides
    warning: boolean;
}

const oneDay = 24 * 60 * 60 * 1000;

export const newestFirst = (a: MachineHistory, b: MachineHistory) => new Date(b.date).getTime() - new Date(a.date).getTime() || b.id - a.id;

// all maintenances with a starting point, the nearest due first
export function dueItems(machine: Machine, histories: MachineHistory[], machineMaintenances: MachineMaintenance[], serviceLifeUnit: string): DueItem[] {
    const items: DueItem[] = [];
    machineMaintenances.filter(mm => !mm.archivedDate && mm.machineType.id === machine.machineType.id).forEach(mm => {
        // without a performed maintenance there is no starting point for the interval
        const last = histories.find(mh => mh.machineMaintenances?.some(m => m.id === mm.id));
        if (!last) {
            return;
        }
        const candidates: DueItem[] = [];
        if (mm.intervalServiceLife > 0) {
            const remaining = Math.round(mm.intervalServiceLife - (machine.serviceLife - last.serviceLife));
            candidates.push({ name: mm.maintenance.name, remaining, unit: serviceLifeUnit, share: remaining / mm.intervalServiceLife, warning: remaining <= mm.warningThresholdServiceLife });
        }
        if (mm.intervalDays > 0) {
            const remaining = Math.round(mm.intervalDays - (Date.now() - new Date(last.date).getTime()) / oneDay);
            candidates.push({ name: mm.maintenance.name, remaining, unit: "days", share: remaining / mm.intervalDays, warning: remaining <= mm.warningThresholdDays });
        }
        if (candidates.length > 0) {
            // either limit inside its warning threshold makes the maintenance due
            items.push({ ...candidates.sort((a, b) => a.share - b.share)[0], warning: candidates.some(c => c.warning) });
        }
    });
    return items.sort((a, b) => a.share - b.share);
}

export const DailyReportCard: React.FC<Props> = ({ machines, machineHistories, machineMaintenances, fuelTypes, translations }) => {
    const [index, setIndex] = React.useState(0);
    const t = (text: string) => translations ? Translate.text(translations, text) : text;
    if (machines.length === 0) {
        return null;
    }
    const current = Math.min(index, machines.length - 1);
    const machine = machines[current];
    const histories = machineHistories.filter(mh => mh.machine.id === machine.id && !mh.archivedDate).sort(newestFirst);
    const lastReport = histories[0];
    const fuelStop = histories.find(mh => mh.fuelStop && mh.fuelStop.quantity > 0)?.fuelStop;
    const fixedIds = new Set(machineHistories.flatMap(mh => mh.fixedMachineDefects ?? []).map(md => md.id));
    const openDefects = histories.flatMap(mh => mh.machineDefects ?? []).filter(md => !md.archivedDate && !fixedIds.has(md.id));
    // hours or kilometres, depending on the machine type
    const serviceLifeUnit = machine.machineType.serviceLifeUnit?.short ?? "";
    const due = dueItems(machine, histories, machineMaintenances, serviceLifeUnit).slice(0, 2);
    // the fuel type list carries the unit, the one on the history entry might not
    const fuelUnit = fuelStop && (fuelStop.fuelType.unit ?? fuelTypes?.find(ft => ft.id === fuelStop.fuelType.id)?.unit)?.short;
    // consumption since the previous stop of the same fuel type, as the bars in the machine statistics
    const consumption = fuelStop && calculateStatistics(machineHistories, machine.id).filter(s => s.fuelType.id === fuelStop.fuelType.id).pop()?.consumption;
    const [hours, decimal] = machine.serviceLife.toFixed(1).split('.');

    return (
        <figure className="c-logbook">
            <header>
                <div>
                    <small>{t("Daily report")}{lastReport && " · " + new Date(lastReport.date).toLocaleDateString()}</small>
                    <strong>{machine.name}{machine.identification && " · " + machine.identification}</strong>
                </div>
                <div className="c-logbook__meter" aria-label={t("Service Life") + " " + machine.serviceLife + " " + serviceLifeUnit}>
                    {hours.split('').map((d, i) => <span key={i}>{d}</span>)}<span className="is-decimal">{decimal}</span>{serviceLifeUnit && <em>{serviceLifeUnit}</em>}
                </div>
            </header>
            <dl className="c-logbook__rows">
                <div className="c-logbook__row"><dt>{t("Operator")}</dt><dd>{machine.user?.userName ?? <span className="c-logbook__muted">{t("Not assigned")}</span>}</dd></div>
                <div className="c-logbook__row"><dt>{t("Fuel")}</dt><dd>{fuelStop
                    ? <><span className="c-logbook__num">{fuelStop.quantity} {fuelUnit}</span> {fuelStop.fuelType.name}
                        {consumption !== undefined && <span className="c-logbook__muted"> · <span className="c-logbook__num">{format(consumption)} {fuelUnit}</span> / 100 {serviceLifeUnit}</span>}</>
                    : <span className="c-logbook__muted">{t("No fuel stop yet")}</span>}</dd></div>
                <div className="c-logbook__row"><dt>{t("Defect")}</dt><dd>{openDefects.length > 0
                    ? openDefects.map(md => <div key={md.id}>{md.defect}{md.outOfOrderFlag && <span className="c-logbook__pill">{t("Out of order")}</span>}</div>)
                    : <span className="c-logbook__muted">{t("No open defects")}</span>}</dd></div>
            </dl>
            <div className="c-logbook__due">
                <p className="c-logbook__due-title">{t("Upcoming maintenance")}</p>
                {due.length === 0 && <span className="c-logbook__muted">{t("No maintenance recorded yet")}</span>}
                {due.map(item => (
                    <div key={item.name}>
                        <div className="c-logbook__due-item">
                            <b>{item.name}</b>
                            <span className="c-logbook__num">{item.remaining < 0
                                ? t("overdue by") + " " + -item.remaining + " " + t(item.unit)
                                : t("due in") + " " + item.remaining + " " + t(item.unit)}</span>
                        </div>
                        <div className="c-logbook__bar">
                            <i className={item.remaining < 0 ? "is-overdue" : item.warning ? "" : "is-ok"} style={{ width: Math.min(100, Math.max(0, (1 - item.share) * 100)) + "%" }}></i>
                        </div>
                    </div>
                ))}
            </div>
            <div className="c-logbook__pager">
                <span>
                    {machines.length > 1 &&
                        <>
                            <button type="button" onClick={() => setIndex(current - 1)} disabled={current === 0} aria-label={t("Previous machine")}>‹</button>
                            {" "}<span className="c-logbook__num">{current + 1} / {machines.length}</span>{" "}
                            <button type="button" onClick={() => setIndex(current + 1)} disabled={current === machines.length - 1} aria-label={t("Next machine")}>›</button>
                        </>
                    }
                </span>
                <Link to="/Machines/dailyreport">{t("New daily report")}</Link>
            </div>
        </figure>
    );
};
